
import React from 'react';
import { Exercise } from '@/models/LearnTypes';
import BaseExercise from './BaseExercise';
import FlashCardDisplay from './FlashCardDisplay';
import FlashCardControls from './FlashCardControls';
import { useFlashCardManager } from '@/hooks/useFlashCardManager';

interface FlashCardExerciseBaseProps {
  currentExercise: Exercise;
  isAnimating: boolean;
  onNext: (wasSuccess: boolean, wasFailed?: boolean) => void;
  onPrev?: () => void;
  isLastExercise: boolean;
  nextCard: Exercise | null;
  initialAnimation?: boolean;
  totalExercises?: number;
  exerciseNumber?: number;
  frontContent: string;
  backContent: string;
  onClose?: () => void;
  onCardFailed?: (exercise: Exercise) => void;
  onLogActivity?: (type: string, exercise: Exercise) => void;
  // Perfect level tracking
  isPerfect?: boolean;
  onImperfect?: () => void;
  isRecycledWord?: boolean;
}

/**
 * Base component for all flashcard-based exercises
 * Manages the flashcard stack and user interactions
 */
const FlashCardExerciseBase: React.FC<FlashCardExerciseBaseProps> = ({
  currentExercise,
  isAnimating,
  onNext,
  onPrev,
  isLastExercise,
  nextCard,
  initialAnimation = false,
  totalExercises = 0,
  exerciseNumber = 0,
  frontContent,
  backContent,
  onClose,
  onCardFailed,
  onLogActivity,
  // Perfect level tracking with default value
  isPerfect = true,
  onImperfect
}) => {
  // Debug logging for perfect level tracking
  React.useEffect(() => {
    console.debug('[FlashCardExerciseBase] isPerfect state:', isPerfect);
  }, [isPerfect]);
  
  // Use the custom hook for flashcard state management
  const {
    // Core state
    stackedCards,
    isFlipped,
    
    // Animation system
    animationState,
    showExcellentAnimation,
    
    // Actions
    handleCardFlip,
    handleNext: hookHandleNext,
    handleFail: hookHandleFail,
    wasManuallyFlipped,
    failedCards,
    wasFailed
  } = useFlashCardManager({
    currentExercise,
    totalExercises,
    exerciseNumber,
    onNext: (result) => {
      // Pass the success/fail status to the parent's onNext callback
      onNext(result.success, result.failed);
    },
    onCardFailed: onCardFailed
  });
  
  // Handler for next button ("I know this card" or "Continue")
  const handleNext = () => {
    // When successfully completing a card, explicitly log the perfect status
    console.debug('[FlashCardExerciseBase] handleNext called, current perfect status:', isPerfect);
    
    // Check if we're completing the last card
    if (isLastExercise) {
      // Check if there are any failed cards
      if (failedCards.length > 0) {
        // If we have failed cards, ensure the global flag is set
        console.debug('⚠️ [FlashCardExerciseBase] Last card with failed cards - setting hadRecycledWords=true');
        (window as any).__HAD_RECYCLED_WORDS__ = true;
        
        // Also ensure the level is marked as imperfect
        if (onImperfect) {
          console.debug('⚠️ [FlashCardExerciseBase] Marking level as imperfect due to remaining failed cards');
          onImperfect();
        }
      } else {
        // Check if we've ever had failed words in this level attempt
        const hadRecycledWords = (window as any).__HAD_RECYCLED_WORDS__ === true;
        console.debug(`[FlashCardExerciseBase] Completing level with perfect status: ${isPerfect}, hadRecycledWords: ${hadRecycledWords}`);
        
        // Double-check: If we've had failed words, ensure the level is marked as imperfect
        if (hadRecycledWords && onImperfect) {
          console.debug('⚠️ [FlashCardExerciseBase] Final check: Had failed words, marking as imperfect');
          onImperfect();
        }
      }
    }
    
    // Call the hook's handleNext
    hookHandleNext();
  };
  
  // Handler for the "Practice more" button
  const handleFail = () => {
    // Set the global flag when "Practice more" is clicked
    console.debug('⚠️⚠️ [FlashCardExerciseBase] Setting global hadRecycledWords=true - Practice more clicked');
    (window as any).__HAD_RECYCLED_WORDS__ = true;
    
    // Mark level as imperfect when "Practice more" is clicked
    if (onImperfect) {
      console.debug('[FlashCardExerciseBase] Marked as imperfect - Practice more clicked');
      onImperfect();
    }
    
    hookHandleFail();
  };
  
  // Log activity function to pass to FlashCardControls
  const handleLogActivity = (type: string, exercise: Exercise) => {
    console.log('FlashCardExerciseBase forwarding activity logging:', type, exercise.id);
    if (onLogActivity) {
      onLogActivity(type, exercise);
    }
  };
  
  return (
    <BaseExercise
      id={`flashcard-exercise-${currentExercise.id}`}
      data-manually-flipped={wasManuallyFlipped}
      currentExercise={currentExercise}
      isAnimating={isAnimating}
      onNext={() => handleNext()}
      isLastExercise={isLastExercise}
      renderExerciseContent={() => (
        <FlashCardDisplay
          // Core content props
          stackedCards={stackedCards}
          frontContent={frontContent}
          backContent={backContent}
          currentExerciseId={currentExercise.id}
          isFlipped={isFlipped}
          onFlip={handleCardFlip}
          
          // Animation system props
          animationState={animationState}
        />
      )}
      renderActionButtons={() => (
        <FlashCardControls
          isFlipped={isFlipped}
          isLastExercise={isLastExercise}
          showExcellentAnimation={showExcellentAnimation}
          onNext={() => handleNext()}
          onFail={handleFail}
          currentExerciseId={currentExercise.id} // Pass the current exercise ID to track logged cards
          onLogActivity={onLogActivity ? (type) => onLogActivity(type, currentExercise) : undefined}
        />
      )}
      onClose={onClose}
    />
  );
};

export default FlashCardExerciseBase;
